import { debounce } from 'lodash';

import { areNotificationsSupported, collectUpcomingNotifications } from './notification_utils';

const SHOWN_KEY = 'organice.shownNotifications';
const MAX_TIMEOUT_MS = 2147483647;
const MAX_EVENTS = 50;

let timeoutIds = [];

const loadShown = () => {
  try {
    return JSON.parse(localStorage.getItem(SHOWN_KEY) || '{}');
  } catch (e) {
    return {};
  }
};

const pruneShown = (shown, now) => {
  const cutoff = now - 7 * 24 * 60 * 60 * 1000;
  return Object.keys(shown).reduce((next, id) => {
    if (shown[id] > cutoff) {
      next[id] = shown[id];
    }
    return next;
  }, {});
};

const markShown = (id) => {
  try {
    const shown = pruneShown(loadShown(), Date.now());
    shown[id] = Date.now();
    localStorage.setItem(SHOWN_KEY, JSON.stringify(shown));
  } catch (e) {}
};

const hasBeenShown = (id) => !!loadShown()[id];

const clearTimers = () => {
  timeoutIds.forEach((id) => clearTimeout(id));
  timeoutIds = [];
};

const showNotification = async (event) => {
  const options = {
    body: event.body,
    tag: event.id,
    renotify: true,
    data: { path: event.path, headerId: event.headerId },
    icon: '/organice-192x192.png',
  };

  try {
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 500)),
    ]);
    await registration.showNotification(event.title, options);
  } catch (e) {
    new Notification(event.title, options);
  }
};

const fireNotification = (event) => {
  if (hasBeenShown(event.id)) {
    return;
  }
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return;
  }
  markShown(event.id);
  showNotification(event);
};

const syncSchedule = (state) => {
  clearTimers();

  if (!areNotificationsSupported() || Notification.permission !== 'granted') {
    return;
  }

  const settings = state.base.get('notificationSettings');
  const files = state.org.present.get('files');
  const events = collectUpcomingNotifications(files, settings, new Date()).slice(0, MAX_EVENTS);

  events.forEach((event) => {
    if (hasBeenShown(event.id)) {
      return;
    }

    const delay = Math.max(0, event.fireAt.getTime() - Date.now());
    if (delay > MAX_TIMEOUT_MS) {
      return;
    }

    timeoutIds.push(setTimeout(() => fireNotification(event), delay));
  });
};

export const requestNotificationPermission = async () => {
  if (!areNotificationsSupported()) {
    return 'denied';
  }
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  if (Notification.permission === 'denied') {
    return 'denied';
  }
  return await Notification.requestPermission();
};

export const startNotificationScheduler = (store) => {
  if (!areNotificationsSupported()) {
    return;
  }

  const reschedule = debounce(() => {
    syncSchedule(store.getState());
  }, 1000);

  store.subscribe(reschedule);
  reschedule();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      reschedule();
    }
  });
};
